import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useMatches } from "react-router";
import type { BreadcrumbItemProps, PageExtrasContext, PageHandle, PageHeaderMeta, PageMeta } from "./types";
import { isRecord } from "./utils";

// 导入国际化相关功能
import { translateLocalized, useI18n } from './i18n';

// 导入存储库
import { ReactStorage } from '@gulibs/react-storage';

/**
 * 存储配置选项
 */
export interface UseStorageOptions {
    /** 存储类型 */
    storage?: 'local' | 'session';
    /** 过期时间（毫秒），0 表示永不过期 */
    maxAge?: number;
    /** 是否自动清理过期数据 */
    autoCleanup?: boolean;
    /** 清理间隔（毫秒），默认 5 分钟 */
    cleanupInterval?: number;
}

/**
 * 基于 react-storage 的 useStorage hook - 支持过期时间
 * 
 * @param key 存储键
 * @param defaultValue 默认值
 * @param options 配置选项
 * @returns [value, setValue, removeValue, setWithExpiry, isExpired, cleanup]
 */
export function useStorage<T>(
    key: string,
    defaultValue: T,
    options: UseStorageOptions = {}
): [
        T,
        (value: T) => void,
        () => void,
        (value: T, maxAge?: number) => void,
        () => boolean,
        () => void
    ] {
    const {
        storage = 'local',
        maxAge = 0,
        autoCleanup = true,
        cleanupInterval = 5 * 60 * 1000  // 5 分钟
    } = options;

    // 创建存储实例
    const storageInstance = useMemo(() => {
        const instance = ReactStorage.getInstance();
        instance.type = storage === 'session' ? 'sessionStorage' : 'localStorage';
        return instance;
    }, [storage]);

    // 获取初始值
    const getStoredValue = useCallback(() => {
        try {
            const stored = storageInstance.get(key);
            return stored !== null ? stored : defaultValue;
        } catch {
            return defaultValue;
        }
    }, [storageInstance, key, defaultValue]);

    const [value, setValue] = useState<T>(getStoredValue);

    // 设置值（使用默认过期时间）
    const setStoredValue = useCallback((newValue: T) => {
        try {
            setValue(newValue);
            if (maxAge > 0) {
                // 如果设置了默认过期时间，使用过期存储
                storageInstance.set(key, newValue, Date.now() + maxAge);
            } else {
                // 永久存储
                storageInstance.set(key, newValue);
            }

            // 触发认证状态变化事件（仅对认证相关的键）
            if (key.includes('auth_')) {
                dispatchAuthStateChange();
            }
        } catch {
            // Silently handle storage errors
        }
    }, [storageInstance, key, maxAge]);

    // 设置值并指定过期时间
    const setWithExpiry = useCallback((newValue: T, expiryMs?: number) => {
        try {
            setValue(newValue);
            if (expiryMs && expiryMs > 0) {
                // 使用指定的过期时间
                storageInstance.set(key, newValue, Date.now() + expiryMs);
            } else {
                // 永久存储
                storageInstance.set(key, newValue);
            }

            // 触发认证状态变化事件（仅对认证相关的键）
            if (key.includes('auth_')) {
                dispatchAuthStateChange();
            }
        } catch {
            // Silently handle storage errors
        }
    }, [storageInstance, key]);

    // 移除值
    const removeValue = useCallback(() => {
        try {
            setValue(defaultValue);
            storageInstance.remove(key);

            // 触发认证状态变化事件（仅对认证相关的键）
            if (key.includes('auth_')) {
                dispatchAuthStateChange();
            }
        } catch {
            // Silently handle storage errors
        }
    }, [storageInstance, key, defaultValue]);

    // 检查是否过期
    const isExpired = useCallback(() => {
        try {
            // 尝试获取值，如果已过期会返回 null
            const stored = storageInstance.get(key);
            return stored === null && getStoredValue() !== defaultValue;
        } catch {
            return false;
        }
    }, [storageInstance, key, getStoredValue, defaultValue]);

    // 清理过期数据
    const cleanup = useCallback(() => {
        try {
            storageInstance.removeExpired();
        } catch {
            // Silently handle cleanup errors
        }
    }, [storageInstance]);

    // 自动清理过期数据
    useEffect(() => {
        if (autoCleanup && cleanupInterval > 0) {
            // 立即清理一次
            cleanup();

            // 设置定期清理
            const intervalId = setInterval(cleanup, cleanupInterval);
            return () => clearInterval(intervalId);
        }
    }, [autoCleanup, cleanupInterval, cleanup]);

    // 监听存储变化（跨标签页同步）
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === key && e.storageArea === (storage === 'session' ? sessionStorage : localStorage)) {
                try {
                    const newValue = e.newValue ? JSON.parse(e.newValue) : defaultValue;
                    setValue(newValue);
                } catch {
                    // Silently handle parsing errors
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [key, storage, defaultValue, setValue]);

    // 定期检查当前值是否过期
    useEffect(() => {
        if (maxAge > 0) {
            const checkExpiry = () => {
                const currentStored = storageInstance.get(key);
                if (currentStored === null && value !== defaultValue) {
                    // 数据已过期，重置为默认值
                    setValue(defaultValue);
                }
            };

            const intervalId = setInterval(checkExpiry, Math.min(60000, maxAge / 10)); // 每分钟或过期时间的 1/10 检查一次
            return () => clearInterval(intervalId);
        }
    }, [storageInstance, key, value, defaultValue, maxAge, setValue]);

    return [value, setStoredValue, removeValue, setWithExpiry, isExpired, cleanup];
}

// 导入类型（避免重复定义）
import type {
    AuthActions,
    AuthState,
    UseUserOptions,
    BaseUser
} from './types';

/**
 * 触发认证状态变化事件
 */
function dispatchAuthStateChange() {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('authStateChange'));
    }
}

/**
 * 用户状态管理 Hook
 */
export function useUser<TUser extends BaseUser = BaseUser, TCredentials = unknown>(
    options: UseUserOptions<TUser, TCredentials> = {}
): AuthState<TUser> & AuthActions<TUser, TCredentials> {
    const {
        storagePrefix = 'auth',
        useSessionStorage = false,
        fetchUser,
        loginApi,
        refreshTokenApi,
        autoRefreshInterval = 0,
        isTokenExpired,
        isRefreshTokenExpired,
        fieldMapping,
        transformers
    } = options;

    // 默认字段映射
    const fields = useMemo(() => ({
        userField: 'user',
        tokenField: 'token',
        refreshTokenField: 'refreshToken',
        ...fieldMapping
    }), [fieldMapping]);

    // 持久化存储
    const [user, setUser, removeUser] = useStorage<TUser | null>(
        `${storagePrefix}_user`,
        null,
        { storage: useSessionStorage ? 'session' : 'local' }
    );

    const [token, setTokenStorage, removeToken] = useStorage<string | null>(
        `${storagePrefix}_token`,
        null,
        { storage: useSessionStorage ? 'session' : 'local' }
    );

    const [refreshTokenValue, setRefreshTokenStorage, removeRefreshToken] = useStorage<string | null>(
        `${storagePrefix}_refresh_token`,
        null,
        { storage: useSessionStorage ? 'session' : 'local' }
    );

    // 本地状态
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // 计算认证状态
    const isAuthenticated = useMemo(() => {
        if (!token || !user) return false;

        // 检查token是否过期
        if (isTokenExpired && isTokenExpired(token)) {
            return false;
        }

        return true;
    }, [token, user, isTokenExpired]);

    /**
     * 数据转换
     */
    const transformUserData = useCallback((userData: unknown, direction: 'store' | 'restore'): TUser => {
        if (direction === 'store' && transformers?.beforeStore) {
            return transformers.beforeStore(userData as TUser) as TUser;
        }
        if (direction === 'restore' && transformers?.afterRestore) {
            return transformers.afterRestore(userData);
        }
        return userData as TUser;
    }, [transformers]);

    /**
     * 解析API响应
     */
    const parseApiResponse = useCallback((response: Record<string, unknown>) => {
        const userData = response[fields.userField];
        const token = response[fields.tokenField];
        const refreshToken = response[fields.refreshTokenField];

        return {
            user: userData ? (transformers?.transformUser ? transformers.transformUser(userData) : userData) : null,
            token: token || null,
            refreshToken: refreshToken || null
        };
    }, [fields, transformers]);

    /**
     * 存储用户数据
     */
    const storeUserData = useCallback((userData: TUser) => {
        const transformedData = transformUserData(userData, 'store');
        setUser(transformedData);
        // 触发认证状态变化事件
        dispatchAuthStateChange();
    }, [setUser, transformUserData]);

    /**
     * 读取用户数据
     */
    const getUserData = useMemo(() => {
        if (!user) return null;
        return transformUserData(user, 'restore');
    }, [user, transformUserData]);

    /**
     * 设置Token
     */
    const setToken = useCallback((newToken: string) => {
        setTokenStorage(newToken);
        // 触发认证状态变化事件
        dispatchAuthStateChange();
    }, [setTokenStorage]);

    /**
     * 设置RefreshToken
     */
    const setRefreshToken = useCallback((newRefreshToken: string) => {
        setRefreshTokenStorage(newRefreshToken);
        // 触发认证状态变化事件
        dispatchAuthStateChange();
    }, [setRefreshTokenStorage]);

    /**
     * 登录
     */
    const login = useCallback(async (credentials: TCredentials) => {
        if (!loginApi) {
            throw new Error('Login API function not provided');
        }

        setIsLoading(true);
        setError(null);

        try {
            // 转换凭据
            const transformedCredentials = transformers?.transformCredentials
                ? transformers.transformCredentials(credentials)
                : credentials;

            const response = await loginApi(transformedCredentials as TCredentials);

            // 解析响应
            const parsed = parseApiResponse(response);

            // 保存数据
            if (parsed.user) {
                storeUserData(parsed.user as TUser);
            }
            if (parsed.token) {
                setTokenStorage(parsed.token as string);
            }
            if (parsed.refreshToken) {
                setRefreshTokenStorage(parsed.refreshToken as string);
            }

            // 触发认证状态变化事件
            dispatchAuthStateChange();

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Login failed';
            setError(errorMessage);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [loginApi, transformers, parseApiResponse, storeUserData, setTokenStorage, setRefreshTokenStorage]);

    /**
     * 登出
     */
    const logout = useCallback(() => {
        // 清除存储的数据
        removeUser();
        removeToken();
        removeRefreshToken();

        // 重置状态
        setError(null);
        setIsLoading(false);

        // 触发认证状态变化事件
        dispatchAuthStateChange();
    }, [removeUser, removeToken, removeRefreshToken]);

    /**
     * 刷新用户信息
     */
    const refreshUser = useCallback(async () => {
        if (!token || !fetchUser) {
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const updatedUser = await fetchUser(token);
            storeUserData(updatedUser);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to refresh user';
            setError(errorMessage);

            // token可能过期了，清除状态
            if (err instanceof Error && err.message.includes('401')) {
                logout();
            }
        } finally {
            setIsLoading(false);
        }
    }, [token, fetchUser, storeUserData, logout]);

    /**
     * 刷新Token
     */
    const refreshTokenAction = useCallback(async () => {
        if (!refreshTokenValue || !refreshTokenApi) {
            throw new Error('Refresh token or refresh API not available');
        }

        // 检查refreshToken是否过期
        if (isRefreshTokenExpired && isRefreshTokenExpired(refreshTokenValue)) {
            logout();
            throw new Error('Refresh token expired');
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await refreshTokenApi(refreshTokenValue);

            // 解析响应
            const parsed = parseApiResponse(response);

            // 更新token
            if (parsed.token) {
                setTokenStorage(parsed.token as string);
            }

            // 更新refreshToken
            if (parsed.refreshToken) {
                setRefreshTokenStorage(parsed.refreshToken as string);
            }

            // 更新用户信息
            if (parsed.user) {
                storeUserData(parsed.user as TUser);
            }

            // 触发认证状态变化事件
            dispatchAuthStateChange();

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to refresh token';
            setError(errorMessage);

            // 刷新失败，清除认证状态
            logout();
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [refreshTokenValue, refreshTokenApi, isRefreshTokenExpired, logout, parseApiResponse, setTokenStorage, setRefreshTokenStorage, storeUserData]);

    /**
     * 更新用户信息
     */
    const updateUser = useCallback((userData: Partial<TUser>) => {
        if (!getUserData) return;

        const updatedUser = { ...getUserData, ...userData };
        storeUserData(updatedUser);
    }, [getUserData, storeUserData]);

    /**
     * 清除错误
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // 自动刷新用户信息
    useEffect(() => {
        if (autoRefreshInterval > 0 && isAuthenticated && fetchUser) {
            const interval = setInterval(() => {
                refreshUser();
            }, autoRefreshInterval);

            return () => clearInterval(interval);
        }
    }, [autoRefreshInterval, isAuthenticated, fetchUser, refreshUser]);

    // 检查token过期，自动刷新
    useEffect(() => {
        if (token && isTokenExpired && isTokenExpired(token)) {
            if (refreshTokenValue && refreshTokenApi) {
                // 尝试自动刷新token
                refreshTokenAction().catch(() => {
                    // 刷新失败，logout已处理
                });
            } else {
                logout();
            }
        }
    }, [token, isTokenExpired, refreshTokenValue, refreshTokenApi, refreshTokenAction, logout]);

    return {
        // 状态
        user: getUserData,
        token,
        refreshToken: refreshTokenValue,
        isAuthenticated,
        isLoading,
        error,

        // 操作
        login,
        logout,
        refreshUser,
        refreshTokenAction,
        updateUser,
        setToken,
        setRefreshToken,
        clearError
    };
}

/**
 * 只读的用户状态 Hook
 */
export function useUserState<TUser extends BaseUser = BaseUser>(storagePrefix = 'auth', useSessionStorage = false) {
    const [user] = useStorage<TUser | null>(
        `${storagePrefix}_user`,
        null,
        { storage: useSessionStorage ? 'session' : 'local' }
    );

    const [token] = useStorage<string | null>(
        `${storagePrefix}_token`,
        null,
        { storage: useSessionStorage ? 'session' : 'local' }
    );

    const [refreshToken] = useStorage<string | null>(
        `${storagePrefix}_refresh_token`,
        null,
        { storage: useSessionStorage ? 'session' : 'local' }
    );

    const isAuthenticated = useMemo(() => {
        return !!(token && user);
    }, [token, user]);

    return {
        user,
        token,
        refreshToken,
        isAuthenticated
    };
}

/**
 * Token管理 Hook
 */
export function useAuthToken(storagePrefix = 'auth', useSessionStorage = false) {
    const [token, setTokenStorage, removeToken] = useStorage<string | null>(
        `${storagePrefix}_token`,
        null,
        { storage: useSessionStorage ? 'session' : 'local' }
    );

    const [refreshToken, setRefreshTokenStorage, removeRefreshToken] = useStorage<string | null>(
        `${storagePrefix}_refresh_token`,
        null,
        { storage: useSessionStorage ? 'session' : 'local' }
    );

    const setToken = useCallback((newToken: string) => {
        setTokenStorage(newToken);
    }, [setTokenStorage]);

    const setRefreshToken = useCallback((newRefreshToken: string) => {
        setRefreshTokenStorage(newRefreshToken);
    }, [setRefreshTokenStorage]);

    const clearToken = useCallback(() => {
        removeToken();
    }, [removeToken]);

    const clearRefreshToken = useCallback(() => {
        removeRefreshToken();
    }, [removeRefreshToken]);

    const clearAllTokens = useCallback(() => {
        removeToken();
        removeRefreshToken();
    }, [removeToken, removeRefreshToken]);

    return {
        token,
        refreshToken,
        setToken,
        setRefreshToken,
        clearToken,
        clearRefreshToken,
        clearAllTokens,
        hasToken: !!token,
        hasRefreshToken: !!refreshToken
    };
}

/**
 * 面包屑 Hook（支持国际化和路由匹配）
 */
export function useBreadcrumbs<Keys extends string = string>(
    handle?: PageHandle<Keys>,
    options: { enableI18n?: boolean; includeMatches?: boolean } = {}
): BreadcrumbItemProps[] {
    const location = useLocation();
    const matches = useMatches();
    const { enableI18n = false, includeMatches = false } = options;

    // 始终调用 useI18n 以遵循 React Hooks 规则
    const i18n = useI18n();

    const breadcrumbs: BreadcrumbItemProps[] = [];

    if (includeMatches) {
        // 基于路由匹配构建面包屑
        matches.forEach((match) => {
            if (match.handle && isRecord(match.handle)) {
                const matchHandle = match.handle as PageHandle<Keys>;
                if (matchHandle.breadcrumbs) {
                    const breadcrumb: BreadcrumbItemProps = {
                        className: matchHandle.breadcrumbs.className,
                        isDisabled: matchHandle.breadcrumbs.isDisabled as boolean | undefined,
                        isCurrent: match.pathname === location.pathname,
                        startContent: matchHandle.breadcrumbs.startContent as React.ReactNode,
                        endContent: matchHandle.breadcrumbs.endContent as React.ReactNode,
                        children: 'Page'
                    };

                    // 如果启用国际化，翻译标题
                    if (enableI18n && i18n && matchHandle.meta?.title) {
                        const translated = translateLocalized(matchHandle.meta.title, i18n.t);
                        breadcrumb.children = translated;
                    } else if (matchHandle.meta?.title && typeof matchHandle.meta.title === 'string') {
                        breadcrumb.children = matchHandle.meta.title;
                    }

                    breadcrumbs.push(breadcrumb);
                }
            }
        });
    } else if (handle?.breadcrumbs) {
        // 使用传入的handle构建单个面包屑
        const breadcrumbConfig = handle.breadcrumbs;

        const baseBreadcrumb: BreadcrumbItemProps = {
            href: breadcrumbConfig.href || location.pathname,
            className: breadcrumbConfig.className,
            isDisabled: breadcrumbConfig.isDisabled as boolean | undefined,
            isCurrent: breadcrumbConfig.isCurrent as boolean | undefined,
            startContent: breadcrumbConfig.startContent as React.ReactNode,
            endContent: breadcrumbConfig.endContent as React.ReactNode,
            children: 'Current Page'
        };

        // 如果启用国际化且有翻译函数，则翻译children
        if (enableI18n && i18n && typeof baseBreadcrumb.children === 'string') {
            const translated = translateLocalized(baseBreadcrumb.children, i18n.t);
            baseBreadcrumb.children = translated;
        }

        breadcrumbs.push(baseBreadcrumb);
    }

    return breadcrumbs;
}

/**
 * 页面元数据 Hook（支持国际化）
 */
export function usePageMeta<Keys extends string = string>(
    handle?: PageHandle<Keys>,
    options: { enableI18n?: boolean } = {}
): PageMeta<Keys> | undefined {
    const { enableI18n = false } = options;
    const i18n = useI18n();

    const meta = handle?.meta;
    if (!meta) return undefined;

    // 如果启用国际化且有翻译函数，则翻译标题和描述
    if (enableI18n && i18n) {
        return {
            title: meta.title ? translateLocalized(meta.title, i18n.t) : undefined,
            description: meta.description ? String(translateLocalized(meta.description, i18n.t)) : undefined
        };
    }

    return meta;
}

/**
 * 使用动态页面标题 Hook
 */
export function useDocumentTitle<Keys extends string = string>(
    handle?: PageHandle<Keys>,
    suffix?: string,
    options: { enableI18n?: boolean } = {}
): () => void {
    const { enableI18n = false } = options;
    const meta = usePageMeta(handle, { enableI18n });

    return useCallback(() => {
        if (meta?.title) {
            const title = typeof meta.title === 'string' ? meta.title : String(meta.title);
            document.title = suffix ? `${title} | ${suffix}` : title;
        }
    }, [meta, suffix]);
}

/**
 * 获取页面handle（支持继承）
 */
export function useHandle<Keys extends string = string>(): PageHandle<Keys> {
    const matches = useMatches();
    const location = useLocation();
    const [meta, setMeta] = useState<PageMeta<Keys>>({});
    const [pageMeta, setPageMeta] = useState<PageHeaderMeta<Keys>>({});
    const [pageExtras, setPageExtras] = useState<React.ReactNode>("");
    const [breadcrumbs, setBreadcrumbs] = useState<Omit<BreadcrumbItemProps, "children">>();

    // 不过滤 matches，我们需要所有路由信息来判断当前路由是否有 handle
    const allMatches = useMemo(() => matches, [matches]);

    // 获取有 handle 的路由
    const matchesWithHandle = useMemo(() => {
        return matches.filter((obj) => {
            return obj.handle !== undefined;
        });
    }, [matches]);

    // 解析查询参数
    const queryParams = useMemo(() => {
        const searchParams = new URLSearchParams(location.search);
        const params: Record<string, string> = {};
        for (const [key, value] of searchParams.entries()) {
            params[key] = value;
        }
        return params;
    }, [location.search]);

    // 提取路由参数
    const routeParams = useMemo(() => {
        const params: Record<string, string> = {};
        matches.forEach(match => {
            if (match.params) {
                Object.assign(params, match.params);
            }
        });
        return params;
    }, [matches]);

    useEffect(() => {
        // 从根路由开始合并所有的handle配置
        let currentMeta: PageMeta<Keys> = {};
        let currentPageMeta: PageHeaderMeta<Keys> = {};
        let currentPageExtras: React.ReactNode = "";
        let currentBreadcrumbs: Omit<BreadcrumbItemProps, "children"> | undefined;

        // 其他配置（meta, pageMeta, pageExtras, breadcrumbs）使用最深层路由的配置
        // 从最深层路由开始，找到第一个有 handle 的路由
        let targetHandle: PageHandle<Keys> | null = null;

        for (let i = allMatches.length - 1; i >= 0; i--) {
            const match = allMatches[i];
            if (match && isRecord(match.handle)) {
                targetHandle = match.handle as PageHandle<Keys>;
                break; // 找到第一个有 handle 的路由就停止
            }
        }

        if (targetHandle) {
            // 使用找到的 handle 配置
            currentMeta = targetHandle.meta || {};
            currentPageMeta = targetHandle.pageMeta || {};

            // 处理 pageExtras，如果是函数则调用并传递上下文，否则直接使用
            if (targetHandle.pageExtras !== undefined) {
                if (typeof targetHandle.pageExtras === 'function') {
                    // 构建上下文对象
                    const context: PageExtrasContext<Keys> = {
                        path: location.pathname,
                        params: routeParams,
                        query: queryParams,
                        matches: allMatches,
                        handle: targetHandle
                    };
                    currentPageExtras = targetHandle.pageExtras(context);
                } else {
                    currentPageExtras = targetHandle.pageExtras;
                }
            } else {
                currentPageExtras = "";
            }

            currentBreadcrumbs = targetHandle.breadcrumbs;
        } else {
            // 没有找到任何 handle，使用空配置
            currentMeta = {};
            currentPageMeta = {};
            currentPageExtras = "";
            currentBreadcrumbs = undefined;
        }

        // 设置状态
        setMeta(currentMeta);
        setPageMeta(currentPageMeta);
        setPageExtras(currentPageExtras);
        setBreadcrumbs(currentBreadcrumbs);
    }, [allMatches, matchesWithHandle, location.pathname, routeParams, queryParams]);

    return {
        meta,
        pageMeta,
        pageExtras,
        breadcrumbs,
        matches: matchesWithHandle
    };
}